import type { ReactElement, ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import type { Project, RegistryDocument } from '../api/types';
import type { ProjectTableSortKey, SortDirection } from '../lib/filters';
import { ProjectRow } from './ProjectRow';
import type { ManualOrderControls } from './ProjectRow';

interface ProjectTableProps {
  projects: readonly Project[];
  registry: RegistryDocument;
  showStream?: boolean;
  onEdit: (project: Project) => void;
  /** Rendered in place of the table when the (filtered) list is empty. */
  emptyState: ReactNode;
  caption?: string;
  /**
   * F-25 — set on «По потокам» only: renders the leading drag-handle column
   * and wires the drop events. Absent on every other view (no column there).
   */
  manualOrder?: ManualOrderControls;
  /**
   * Set on «Все проекты» only (owner request, 2026-10-14): the named columns
   * become sort buttons wired to the URL (`sort` + `dir`). Absent elsewhere —
   * «По потокам» keeps its per-section sort select + manual drag order.
   */
  sortable?: { sortKey: ProjectTableSortKey; direction: SortDirection; onSort: (key: ProjectTableSortKey) => void };
  /**
   * F-28 («Все проекты», «Вручную» only): the GLOBAL position of every
   * project, counted over the FULL unfiltered list — renders the leading
   * «№» column, so with filters on the numbers keep their gaps instead of
   * silently renumbering (the manual order is a property of the whole list).
   */
  positionOf?: ReadonlyMap<string, number>;
  /** F-28 — the row's «наверх»; travels with `positionOf` (same mode, same page). */
  onSendToTop?: (id: string) => void;
}

/** The project table shared by «Все проекты» and «По потокам» (F-15 columns). */
export function ProjectTable({
  projects,
  registry,
  showStream = false,
  onEdit,
  emptyState,
  caption,
  manualOrder,
  sortable,
  positionOf,
  onSendToTop,
}: ProjectTableProps): ReactElement {
  if (projects.length === 0) return <>{emptyState}</>;

  /**
   * One sortable header cell: a real named `<button>` inside the `<th>`
   * (STYLEGUIDE §7), the direction chevron is decorative (`aria-hidden`), and
   * the WHOLE `<th>` carries `aria-sort` — the active column announces its
   * direction, every other sortable one is `none` (the only values ARIA takes).
   * The chevron is muted while inactive and shows the asc arrow (what the
   * first click does); on the active column it turns `--text-primary` and
   * follows the actual direction.
   */
  const sortHeader = (key: ProjectTableSortKey, label: string, thClass?: string): ReactElement => {
    if (!sortable) {
      return (
        <th scope="col" className={thClass}>
          {label}
        </th>
      );
    }
    const active = sortable.sortKey === key;
    const descending = active && sortable.direction === 'desc';
    const Icon = descending ? ChevronDown : ChevronUp;
    return (
      <th scope="col" className={thClass} aria-sort={descending ? 'descending' : active ? 'ascending' : 'none'}>
        <button
          type="button"
          className={active ? 'project-table__sort project-table__sort--active' : 'project-table__sort'}
          title={`Сортировать по: ${label}`}
          onClick={() => sortable.onSort(key)}
        >
          {label}
          <Icon size={14} aria-hidden="true" className="project-table__sort-icon" />
        </button>
      </th>
    );
  };

  return (
    <div className="table-wrap">
      <table className={manualOrder?.enabled ? 'data project-table project-table--manual' : 'data project-table'}>
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead>
          <tr>
            {positionOf ? (
              // Not a sort button on purpose: the «№» column displays the
              // stored manual order, it does not change the sort — «Вручную»
              // is picked in the toolbar select.
              <th scope="col" className="project-table__pos num">
                №
              </th>
            ) : null}
            {manualOrder ? (
              <th scope="col" className="project-table__drag" aria-label="Ручной порядок" />
            ) : null}
            <th scope="col" className="project-table__risk" aria-label="At-risk" />
            {sortHeader('title', 'Проект')}
            {sortHeader('status', 'Статус')}
            {sortHeader('priority', 'Приоритет')}
            {sortHeader('category', 'Категория')}
            <th scope="col">Исполнители</th>
            {sortHeader('dueDate', 'Срок до')}
            {sortHeader('hours', 'Часы', 'num')}
            <th scope="col" className="num" aria-label="Действия" />
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <ProjectRow
              key={project.id}
              project={project}
              registry={registry}
              showStream={showStream}
              onEdit={onEdit}
              manualOrder={manualOrder}
              position={positionOf ? (positionOf.get(project.id) ?? null) : undefined}
              onSendToTop={onSendToTop}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
